import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { 
  ArrowLeft, 
  BarChart3, 
  Users,
  Trophy,
  Activity,
  Save,
  RefreshCw
} from 'lucide-react';

export default function AdminPlatformStats() {
  const [stats, setStats] = useState({
    total_participants: 100,
    completed_events: 1,
    community_status: 'Activa'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const fetchStats = async () => {
    setLoading(true);
    try {
      const data = await api.getPlatformStats();
      setStats({
        total_participants: data.total_participants || 100,
        completed_events: data.completed_events || 1,
        community_status: data.community_status || 'Activa'
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.updatePlatformStats(stats);
      toast.success('Estadísticas actualizadas correctamente');
    } catch (error) {
      toast.error('Error al guardar estadísticas');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b sticky top-16 z-40">
        <div className="max-w-3xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/admin">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Estadísticas de Plataforma</h1>
                <p className="text-sm text-slate-500">Métricas visibles en la página pública</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-100">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <BarChart3 className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Bloque de Confianza</p>
                <p className="text-sm text-blue-700">
                  Estas estadísticas se muestran en la página principal y en la sección de transparencia. 
                  Actualiza manualmente los valores según los datos reales de la plataforma.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Stats Form */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Activity className="w-5 h-5 text-emerald-500" />
              Métricas de la Comunidad
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {loading ? (
              <div className="py-8 text-center">
                <RefreshCw className="w-8 h-8 animate-spin mx-auto text-slate-400" />
              </div>
            ) : (
              <>
                {/* Total Participants */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Users className="w-4 h-4 inline mr-2" />
                    Total de Participantes Registrados
                  </label>
                  <Input
                    type="number"
                    value={stats.total_participants}
                    onChange={(e) => setStats({ ...stats, total_participants: parseInt(e.target.value) || 0 })}
                    placeholder="100"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Se mostrará como "+{stats.total_participants} participantes"
                  </p>
                </div>

                {/* Completed Events */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Trophy className="w-4 h-4 inline mr-2" />
                    Eventos Completados
                  </label>
                  <Input
                    type="number"
                    value={stats.completed_events}
                    onChange={(e) => setStats({ ...stats, completed_events: parseInt(e.target.value) || 0 })}
                    placeholder="1"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Número de eventos/sorteos que han finalizado exitosamente
                  </p>
                </div>

                {/* Community Status */}
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    <Activity className="w-4 h-4 inline mr-2" />
                    Estado de la Comunidad
                  </label>
                  <select
                    value={stats.community_status}
                    onChange={(e) => setStats({ ...stats, community_status: e.target.value })}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  >
                    <option value="Activa">Activa</option>
                    <option value="En crecimiento">En crecimiento</option>
                    <option value="Consolidada">Consolidada</option>
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    Descripción del estado actual de la comunidad
                  </p>
                </div>

                {/* Save Button */}
                <Button 
                  onClick={handleSave} 
                  disabled={saving}
                  className="w-full btn-primary"
                >
                  {saving ? (
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Guardar Cambios
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        {/* Preview */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Vista Previa</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-slate-500 mb-4">
              Así se verán las estadísticas en la página pública:
            </p>
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-emerald-600">+{stats.total_participants}</p>
                <p className="text-xs text-slate-600">Participantes</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-blue-600">{stats.completed_events}</p>
                <p className="text-xs text-slate-600">Eventos</p>
              </div>
              <div className="text-center p-4 bg-slate-50 rounded-xl">
                <p className="text-2xl font-bold text-purple-600">{stats.community_status}</p>
                <p className="text-xs text-slate-600">Comunidad</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
